import { useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { usePostHog } from 'posthog-js/react'

/**
 * Hook to identify users in PostHog when they log in via Auth0
 * Uses the user's email as the identifier
 * Only identifies once per user session to avoid duplicate calls
 * Tracks logout events and resets PostHog session on logout
 */
export const usePostHogIdentify = () => {
  const { user, isAuthenticated } = useAuth0()
  const posthog = usePostHog()
  const identifiedRef = useRef(false)

  useEffect(() => {
    // Only identify if user is authenticated, has email, PostHog is available, and hasn't been identified yet
    if (isAuthenticated && user?.email && posthog && !identifiedRef.current) {
      try {
        // Identify user in PostHog using email
        posthog.identify(user.email, {
          email: user.email,
          name: user.name,
          // Add any other user properties you want to track
        })
        identifiedRef.current = true
      } catch (error) {
        console.error('Failed to identify user in PostHog:', error)
      }
    }

    // Track logout event and reset PostHog when user logs out
    // Only trigger if user was previously identified (to avoid false positives on initial load)
    if (!isAuthenticated && identifiedRef.current && posthog) {
      try {
        // Reset PostHog to clear user identification and prevent data leakage
        posthog.reset()
        identifiedRef.current = false
      } catch (error) {
        console.error('Failed to track logout in PostHog:', error)
      }
    }
  }, [isAuthenticated, user, posthog])
}

